import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    IconButton,
    Card,
    CardMedia,
    CardContent,
    CardActions,
    Chip,
    Grid,
    TextField,
    InputAdornment,
    Button,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";

import MobileContainer from "../global/components/MobileContainer";
import { ProductsLoader } from "../components/ProductsLoader";
import { useProducts } from "../hooks/useProducts";
import { useShoppingListRedux } from "../hooks/useShoppingListRedux";
import { MAIN_COLOR_RED } from "../assets/styles";
import { useNavigate, useParams } from "react-router-dom";
import { SavedShoppingList } from "../redux/shoppingListSlice";
import { RawProduct } from "../types";
import { shoppingListApi } from "../api";

const EditShoppingListPage: React.FC = () => {
    const { listId } = useParams<{ listId: string }>();
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState("");
    const [editingList, setEditingList] = useState<SavedShoppingList | null>(null);
    const [listItems, setListItems] = useState<Record<string, number>>({});
    const [listItemIds, setListItemIds] = useState<Record<string, number[]>>({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const {
        savedLists,
        updateSavedList,
    } = useShoppingListRedux();

    const { products } = useProducts();

    // Load the list to edit from API
    useEffect(() => {
        const loadList = async () => {
            if (!listId) {
                navigate('/lists');
                return;
            }

            setLoading(true);
            setError(null);

            try {
                const response = await shoppingListApi.getShoppingListById(listId, true);
                const list = response.list;
                setEditingList(list);
                setListItems({ ...list.items });
                const detailed = await shoppingListApi.getShoppingListItemsDetailed(listId);
                if (detailed?.products) {
                    const idsByProduct: Record<string, number[]> = {};
                    detailed.products.forEach((p) => {
                        idsByProduct[p.product_id] = [...(p.item_ids || [])];
                    });
                    setListItemIds(idsByProduct);
                } else {
                    setListItemIds({});
                }
            } catch (err) {
                console.error('Error loading shopping list:', err);
                setError('Failed to load shopping list');
                // Fallback to Redux state if API fails
                const list = savedLists.find(l => l.id === listId);
                if (list) {
                    setEditingList(list);
                    setListItems({ ...list.items });
                    setListItemIds({});
                    setError(null);
                } else {
                    // List not found, navigate back
                    navigate('/lists');
                }
            } finally {
                setLoading(false);
            }
        };

        loadList();
    }, [listId, navigate, savedLists]);

    // Filter products based on search
    const filteredProducts = products.filter((product: RawProduct) => {
        return product.title.toLowerCase().includes(searchTerm.toLowerCase());
    });

    // Get products that are in the list
    const listProductIds = Object.keys(listItems);
    const listProducts = products.filter(p => listProductIds.includes(p.unique_id));
    const otherProducts = filteredProducts.filter(p => !listProductIds.includes(p.unique_id));

    const handleAddItem = (productId: string) => {
        setListItems(prev => ({
            ...prev,
            [productId]: (prev[productId] || 0) + 1
        }));
    };

    const handleRemoveItem = async (productId: string) => {
        if (!editingList) return;
        const currentQty = listItems[productId] ?? 0;
        if (currentQty <= 0) return;
        if (currentQty === 1) {
            await removeProductFromList(editingList.id, productId);
        } else {
            const ids = listItemIds[productId] ?? [];
            if (ids.length > 0) {
                const itemId = ids[0];
                setError(null);
                try {
                    await shoppingListApi.deleteShoppingListItemByItemId(editingList.id, itemId);
                    setListItemIds(prev => ({
                        ...prev,
                        [productId]: (prev[productId] ?? []).slice(1),
                    }));
                    setListItems(prev => ({ ...prev, [productId]: prev[productId] - 1 }));
                } catch (err) {
                    console.error('Failed to remove item:', err);
                    setError('Failed to remove item. Please try again.');
                }
            } else {
                setListItems(prev => ({ ...prev, [productId]: prev[productId] - 1 }));
            }
        }
    };

    const handleDeleteItem = async (productId: string) => {
        if (!editingList) return;
        await removeProductFromList(editingList.id, productId);
    };

    const removeProductFromList = async (listId: string, productId: string) => {
        setError(null);
        try {
            await shoppingListApi.deleteShoppingListItem(listId, productId);
            setListItems(prev => {
                const next = { ...prev };
                delete next[productId];
                return next;
            });
            setListItemIds(prev => {
                const next = { ...prev };
                delete next[productId];
                return next;
            });
        } catch (err) {
            console.error('Failed to remove item:', err);
            setError('Failed to remove item. Please try again.');
        }
    };

    const handleSave = async () => {
        if (!editingList) return;
        setError(null);
        try {
            const response = await shoppingListApi.saveListForEdit(
                editingList.id,
                editingList.name,
                listItems
            );
            updateSavedList(response.list);
            navigate('/lists');
        } catch (err) {
            console.error('Error saving shopping list:', err);
            setError('Failed to save shopping list');
        }
    };

    const handleCancel = () => {
        navigate('/lists');
    };

    const handleClearAll = async () => {
        if (!editingList) return;
        if (Object.keys(listItems).length === 0) return;
        if (!window.confirm('Clear all items from this list? This cannot be undone.')) return;
        setError(null);
        try {
            await shoppingListApi.deleteAllShoppingListItems(editingList.id);
            setListItems({});
            setListItemIds({});
        } catch (err) {
            console.error('Error clearing list:', err);
            setError('Failed to clear items. Please try again.');
        }
    };

    const getProductPrice = (product: RawProduct, quantity: number): number => {
        return parseFloat(product.sale_price) * quantity;
    };

    const calculateTotal = (): number => {
        return listProducts.reduce((sum, product) => {
            const quantity = listItems[product.unique_id] || 0;
            return sum + getProductPrice(product, quantity);
        }, 0);
    };

    if (loading || !editingList) {
        return (
            <ProductsLoader>
                <MobileContainer>
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography>Loading shopping list...</Typography>
                        {error && (
                            <Typography color="error" sx={{ mt: 2 }}>
                                {error}
                            </Typography>
                        )}
                    </Box>
                </MobileContainer>
            </ProductsLoader>
        );
    }

    return (
        <ProductsLoader>
            <MobileContainer>
                <Box sx={{ pb: 2 }}>
                    {/* Header */}
                    <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                        <IconButton
                            onClick={() => navigate('/lists')}
                            sx={{ mr: 1 }}
                        >
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography variant="h5" fontWeight="bold" sx={{ flex: 1 }}>
                            Edit: {editingList.name}
                        </Typography>
                    </Box>

                    {/* Search Bar */}
                    <TextField
                        fullWidth
                        placeholder="Search products..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{ mb: 3 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />

                    {/* List Summary */}
                    <Card sx={{ mb: 3, backgroundColor: '#f5f5f5' }}>
                        <CardContent>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <Box>
                                    <Typography variant="body2" color="text.secondary">
                                        {Object.values(listItems).reduce((sum, qty) => sum + qty, 0)} items
                                    </Typography>
                                    <Typography variant="h6" fontWeight="bold">
                                        Total: €{calculateTotal().toFixed(2)}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: "flex", gap: 1, flexWrap: 'wrap' }}>
                                    {Object.keys(listItems).length > 0 && (
                                        <Button
                                            variant="outlined"
                                            onClick={handleClearAll}
                                            sx={{
                                                borderColor: MAIN_COLOR_RED,
                                                color: MAIN_COLOR_RED,
                                                '&:hover': {
                                                    borderColor: MAIN_COLOR_RED,
                                                    backgroundColor: 'rgba(220, 38, 38, 0.04)',
                                                }
                                            }}
                                        >
                                            Clear all
                                        </Button>
                                    )}
                                    <Button
                                        variant="outlined"
                                        onClick={handleCancel}
                                        sx={{
                                            borderColor: MAIN_COLOR_RED,
                                            color: MAIN_COLOR_RED,
                                            '&:hover': {
                                                borderColor: MAIN_COLOR_RED,
                                                backgroundColor: 'rgba(220, 38, 38, 0.04)',
                                            }
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="contained"
                                        onClick={handleSave}
                                        sx={{
                                            backgroundColor: MAIN_COLOR_RED,
                                            '&:hover': {
                                                backgroundColor: MAIN_COLOR_RED,
                                            }
                                        }}
                                    >
                                        Save
                                    </Button>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Products in List */}
                    {listProducts.length > 0 && (
                        <Box sx={{ mb: 4 }}>
                            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                                Products in List ({listProducts.length})
                            </Typography>
                            <Grid container spacing={2}>
                                {listProducts.map((product) => {
                                    const quantity = listItems[product.unique_id] || 0;
                                    return (
                                        <Grid item xs={12} key={product.unique_id}>
                                            <Card>
                                                <Box sx={{ display: "flex" }}>
                                                    <CardMedia
                                                        component="img"
                                                        sx={{ width: 100, height: 100, objectFit: "contain", p: 1 }}
                                                        image={product.image_url}
                                                        alt={product.title}
                                                    />
                                                    <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                                                        <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
                                                            {product.title}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                                                            {product.shortDescription}
                                                        </Typography>
                                                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                                            <Typography variant="h6" color={MAIN_COLOR_RED} fontWeight="bold">
                                                                €{parseFloat(product.sale_price).toFixed(2)}
                                                            </Typography>
                                                            <Chip
                                                                label={`€${getProductPrice(product, quantity).toFixed(2)}`}
                                                                size="small"
                                                                sx={{ backgroundColor: MAIN_COLOR_RED, color: "white" }}
                                                            />
                                                        </Box>
                                                    </CardContent>
                                                    <CardActions sx={{ flexDirection: "column", justifyContent: "center", gap: 1, pr: 2 }}>
                                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleRemoveItem(product.unique_id)}
                                                                sx={{
                                                                    backgroundColor: MAIN_COLOR_RED,
                                                                    color: "white",
                                                                    minWidth: "40px",
                                                                    height: "40px",
                                                                    '&:hover': {
                                                                        backgroundColor: MAIN_COLOR_RED,
                                                                    }
                                                                }}
                                                            >
                                                                <RemoveIcon fontSize="small" />
                                                            </IconButton>
                                                            <Typography variant="body2" sx={{ minWidth: "30px", textAlign: "center" }}>
                                                                {quantity}
                                                            </Typography>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleAddItem(product.unique_id)}
                                                                sx={{
                                                                    backgroundColor: MAIN_COLOR_RED,
                                                                    color: "white",
                                                                    minWidth: "40px",
                                                                    height: "40px",
                                                                    '&:hover': {
                                                                        backgroundColor: MAIN_COLOR_RED,
                                                                    }
                                                                }}
                                                            >
                                                                <AddIcon fontSize="small" />
                                                            </IconButton>
                                                        </Box>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleDeleteItem(product.unique_id)}
                                                            sx={{
                                                                color: "error.main",
                                                                minWidth: "40px",
                                                                height: "40px",
                                                            }}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </CardActions>
                                                </Box>
                                            </Card>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </Box>
                    )}

                    {/* Other Products (to add) */}
                    {otherProducts.length > 0 && (
                        <Box>
                            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                                Add Products ({otherProducts.length})
                            </Typography>
                            <Grid container spacing={2}>
                                {otherProducts.map((product) => (
                                    <Grid item xs={12} key={product.unique_id}>
                                        <Card>
                                            <Box sx={{ display: "flex" }}>
                                                <CardMedia
                                                    component="img"
                                                    sx={{ width: 100, height: 100, objectFit: "contain", p: 1 }}
                                                    image={product.image_url}
                                                    alt={product.title}
                                                />
                                                <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                                                    <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
                                                        {product.title}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                                                        {product.shortDescription}
                                                    </Typography>
                                                    <Typography variant="h6" color={MAIN_COLOR_RED} fontWeight="bold">
                                                        €{parseFloat(product.sale_price).toFixed(2)}
                                                    </Typography>
                                                </CardContent>
                                                <CardActions sx={{ alignItems: "center", pr: 2 }}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleAddItem(product.unique_id)}
                                                        sx={{
                                                            backgroundColor: MAIN_COLOR_RED,
                                                            color: "white",
                                                            minWidth: "40px",
                                                            width: "40px",
                                                            height: "40px",
                                                            '&:hover': {
                                                                backgroundColor: MAIN_COLOR_RED,
                                                            }
                                                        }}
                                                    >
                                                        <AddIcon fontSize="small" />
                                                    </IconButton>
                                                </CardActions>
                                            </Box>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    )}

                    {listProducts.length === 0 && otherProducts.length === 0 && (
                        <Box sx={{ textAlign: "center", py: 4 }}>
                            <Typography variant="body1" color="text.secondary">
                                {searchTerm ? "No products found" : "No products available"}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </MobileContainer>
        </ProductsLoader>
    );
};

export default EditShoppingListPage;

